import React, { useCallback, useEffect, useState } from 'react';
import ModalWrapper from '../shared/ModalWrapper/wrapper';
import Style from './style.module.css';
import {
  addMailingListRecipient,
  createMailingList,
  deleteMailingList,
  mailingLists as mailingListsQuery,
  removeMailingListRecipient,
  renameMailingList,
  updateMailingListRecipient,
} from '../../gql';

const blankRecipient = () => ({
  name: '',
  email: '',
  type: 'collective',
  category: '',
  org: '',
});

export const modalStyles = {
  overlay: { backgroundColor: 'rgba(0, 0, 0, 0.55)', zIndex: 20 },
  content: {
    width: 'min(460px, calc(100vw - 32px))',
    inset: '50% auto auto 50%',
    transform: 'translate(-50%, -50%)',
    padding: '28px',
    border: 'none',
    borderRadius: '10px',
    boxSizing: 'border-box',
  },
};

const getGraphqlData = (response) => {
  const error = response.data?.errors?.[0];
  if (error)
    throw new Error(error.message || 'Could not save the mailing list.');
  return response.data?.data || {};
};

const MailingListManager = ({ request, adminToken }) => {
  const [lists, setLists] = useState([]);
  const [newList, setNewList] = useState({
    name: '',
    reportKey: '',
  });
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [modalDraft, setModalDraft] = useState({});
  const [modalError, setModalError] = useState('');
  const [isModalSaving, setIsModalSaving] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const loadLists = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await request(mailingListsQuery, undefined, adminToken);
      setLists(getGraphqlData(response).mailingLists || []);
      return true;
    } catch (error) {
      setIsError(true);
      setMessage(error.message || 'Could not load mailing lists.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [adminToken, request]);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  const run = async (action, successMessage, onSuccess) => {
    setMessage('');
    setIsError(false);
    try {
      const data = await action();
      await loadLists();
      onSuccess?.(data);
      setMessage(successMessage);
      return true;
    } catch (error) {
      setIsError(true);
      setMessage(error.message || 'Could not save the mailing list.');
      return false;
    }
  };

  const createList = async (event) => {
    event.preventDefault();
    const created = await run(async () => {
      const response = await request(createMailingList, newList, adminToken);
      return getGraphqlData(response).createMailingList;
    }, 'Mailing list created.');

    if (created) {
      setNewList({ name: '', reportKey: 'PHILADELPHIA_WEEKLY_REPORT' });
    }
  };

  const openModal = (nextModal, draft) => {
    setModal(nextModal);
    setModalDraft(draft);
    setModalError('');
  };

  const closeModal = () => {
    if (isModalSaving) return;
    setModal(null);
    setModalDraft({});
    setModalError('');
  };

  const submitModal = async (event) => {
    event.preventDefault();
    if (!modal) return;

    setModalError('');
    setIsModalSaving(true);

    try {
      let response;
      let successMessage;

      if (modal.kind === 'add-recipient') {
        response = await request(
          addMailingListRecipient,
          { listId: modal.list.id, input: modalDraft },
          adminToken,
        );
        const recipient = getGraphqlData(response).addMailingListRecipient;
        setLists((currentLists) =>
          currentLists.map((list) =>
            list.id === modal.list.id &&
            !list.recipients.some((item) => item.id === recipient.id)
              ? {
                  ...list,
                  recipients: [...list.recipients, recipient].sort((a, b) =>
                    a.name.localeCompare(b.name),
                  ),
                }
              : list,
          ),
        );
        successMessage = 'Recipient added.';
      } else if (modal.kind === 'rename-list') {
        response = await request(
          renameMailingList,
          { id: modal.list.id, name: modalDraft.name },
          adminToken,
        );
        getGraphqlData(response);
        successMessage = 'Mailing list renamed.';
      } else if (modal.kind === 'edit-recipient') {
        response = await request(
          updateMailingListRecipient,
          { id: modal.recipient.id, input: modalDraft },
          adminToken,
        );
        getGraphqlData(response);
        successMessage = 'Recipient updated.';
      } else if (modal.kind === 'delete-list') {
        response = await request(
          deleteMailingList,
          { id: modal.list.id },
          adminToken,
        );
        getGraphqlData(response);
        successMessage = 'Mailing list deleted.';
      } else if (modal.kind === 'remove-recipient') {
        response = await request(
          removeMailingListRecipient,
          { listId: modal.list.id, id: modal.recipient.id },
          adminToken,
        );
        getGraphqlData(response);
        successMessage = 'Recipient removed.';
      }

      await loadLists();
      setMessage(successMessage);
      setIsError(false);
      setModal(null);
      setModalDraft({});
    } catch (error) {
      setModalError(error.message || 'Could not save the mailing list.');
    } finally {
      setIsModalSaving(false);
    }
  };

  const renderModal = () => {
    if (!modal) return null;

    const isConfirmation = ['delete-list', 'remove-recipient'].includes(
      modal.kind,
    );
    const title =
      modal.kind === 'add-recipient'
        ? 'Add recipient'
        : modal.kind === 'rename-list'
          ? 'Rename mailing list'
          : modal.kind === 'edit-recipient'
            ? 'Edit recipient'
            : modal.kind === 'delete-list'
              ? 'Delete mailing list'
              : 'Remove recipient';

    return (
      <ModalWrapper
        isOpen
        closeModal={closeModal}
        style={modalStyles}
        closeButtonColor="#1a1a1a"
      >
        <form className={Style.mailingListModal} onSubmit={submitModal}>
          <h3 className={Style.modalTitle}>{title}</h3>

          {modal.kind === 'rename-list' ? (
            <label className={Style.modalLabel} htmlFor="mailing-list-name">
              List name
              <input
                id="mailing-list-name"
                className={Style.inputText}
                value={modalDraft.name || ''}
                onChange={(event) =>
                  setModalDraft({ ...modalDraft, name: event.target.value })
                }
                required
                autoFocus
              />
            </label>
          ) : null}

          {['add-recipient', 'edit-recipient'].includes(modal.kind) ? (
            <>
              <label className={Style.modalLabel} htmlFor="recipient-name">
                Name
                <input
                  id="recipient-name"
                  className={Style.inputText}
                  value={modalDraft.name || ''}
                  onChange={(event) =>
                    setModalDraft({ ...modalDraft, name: event.target.value })
                  }
                  required
                  autoFocus
                />
              </label>
              <label className={Style.modalLabel} htmlFor="recipient-email">
                Email
                <input
                  id="recipient-email"
                  className={Style.inputText}
                  type="email"
                  value={modalDraft.email || ''}
                  onChange={(event) =>
                    setModalDraft({ ...modalDraft, email: event.target.value })
                  }
                  required
                />
              </label>
              <label className={Style.modalLabel} htmlFor="recipient-type">
                Type
                <select
                  id="recipient-type"
                  className={Style.inputText}
                  value={modalDraft.type || 'collective'}
                  onChange={(event) =>
                    setModalDraft({ ...modalDraft, type: event.target.value })
                  }
                >
                  <option value="collective">Collective</option>
                  <option value="private">Private</option>
                </select>
              </label>
              <label className={Style.modalLabel} htmlFor="recipient-category">
                Category
                <input
                  id="recipient-category"
                  className={Style.inputText}
                  value={modalDraft.category || ''}
                  onChange={(event) =>
                    setModalDraft({
                      ...modalDraft,
                      category: event.target.value,
                    })
                  }
                  required
                />
              </label>
              <label className={Style.modalLabel} htmlFor="recipient-org">
                Organization
                <input
                  id="recipient-org"
                  className={Style.inputText}
                  value={modalDraft.org || ''}
                  onChange={(event) =>
                    setModalDraft({ ...modalDraft, org: event.target.value })
                  }
                  required
                />
              </label>
            </>
          ) : null}

          {modal.kind === 'delete-list' ? (
            <p className={Style.modalCopy}>
              Delete <strong>{modal.list.name}</strong> and every recipient on
              it? This cannot be undone.
            </p>
          ) : null}

          {modal.kind === 'remove-recipient' ? (
            <p className={Style.modalCopy}>
              Remove <strong>{modal.recipient.name}</strong> from this mailing
              list?
            </p>
          ) : null}

          {modalError ? (
            <div className={Style.errorMessage}>{modalError}</div>
          ) : null}

          <div className={Style.modalActions}>
            <button
              type="button"
              className={Style.linkButton}
              onClick={closeModal}
              disabled={isModalSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={
                isConfirmation ? Style.modalDangerButton : Style.sendNowButton
              }
              disabled={isModalSaving}
            >
              {isModalSaving
                ? 'Saving…'
                : modal.kind === 'delete-list'
                  ? 'Delete list'
                  : modal.kind === 'remove-recipient'
                    ? 'Remove recipient'
                    : modal.kind === 'add-recipient'
                      ? 'Add recipient'
                      : 'Save changes'}
            </button>
          </div>
        </form>
      </ModalWrapper>
    );
  };

  return (
    <section
      className={`${Style.mailingListSection} ${isExpanded ? '' : Style.mailingListCollapsed}`}
    >
      <div className={Style.subscriptionsHeader}>
        <div>
          <button
            type="button"
            className={Style.collapseToggle}
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-expanded={isExpanded}
          >
            <i
              className={`pi ${isExpanded ? 'pi-chevron-down' : 'pi-chevron-right'}`}
            />
            <span className={Style.label}>Mailing lists</span>
          </button>
          <div className={Style.reportHint}>
            Manage recipients for each report. Only administrators can view or
            edit these lists.
          </div>
        </div>
        <button
          type="button"
          className={Style.linkButton}
          onClick={loadLists}
          disabled={isLoading}
        >
          {isLoading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {isExpanded ? (
        <>
          {message ? (
            <div
              className={isError ? Style.errorMessage : Style.successMessage}
            >
              {message}
            </div>
          ) : null}

          <form className={Style.mailingListForm} onSubmit={createList}>
            <input
              className={Style.inputText}
              value={newList.name}
              onChange={(event) =>
                setNewList({ ...newList, name: event.target.value })
              }
              placeholder="List name"
              required
            />
            <input
              className={Style.inputText}
              value={newList.reportKey}
              onChange={(event) =>
                setNewList({ ...newList, reportKey: event.target.value })
              }
              placeholder="Report key"
              required
            />
            <button type="submit" className={Style.sendNowButton}>
              Create list
            </button>
          </form>

          {lists.length === 0 && !isLoading ? (
            <div className={Style.reportHint}>
              No mailing lists yet. Create the first list above.
            </div>
          ) : null}

          {lists.map((list) => {
            return (
              <div className={Style.mailingListCard} key={list.id}>
                <div className={Style.subscriptionsHeader}>
                  <div>
                    <strong>{list.name}</strong>
                    <div className={Style.reportHint}>
                      Report key: {list.reportKey}
                    </div>
                  </div>
                  <div className={Style.inlineActions}>
                    <button
                      type="button"
                      className={Style.linkButton}
                      onClick={() =>
                        openModal(
                          { kind: 'add-recipient', list },
                          blankRecipient(),
                        )
                      }
                    >
                      Add recipient
                    </button>
                    <button
                      type="button"
                      className={Style.linkButton}
                      onClick={() =>
                        openModal(
                          { kind: 'rename-list', list },
                          { name: list.name },
                        )
                      }
                    >
                      Rename
                    </button>
                    <button
                      type="button"
                      className={Style.dangerButton}
                      onClick={() =>
                        openModal({ kind: 'delete-list', list }, {})
                      }
                    >
                      Delete
                    </button>
                  </div>
                </div>

                <div className={Style.tableWrapper}>
                  <table
                    className={`${Style.subscriptionsTable} ${Style.mailingListTable}`}
                  >
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Type</th>
                        <th>Category</th>
                        <th>Organization</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.recipients.length === 0 ? (
                        <tr>
                          <td colSpan="6" className={Style.emptyRecipients}>
                            No recipients yet.
                          </td>
                        </tr>
                      ) : null}
                      {list.recipients.map((recipient) => (
                        <tr key={recipient.id}>
                          <td>{recipient.name}</td>
                          <td>{recipient.email}</td>
                          <td>{recipient.type}</td>
                          <td>{recipient.category}</td>
                          <td>{recipient.org}</td>
                          <td className={Style.inlineActions}>
                            <button
                              type="button"
                              className={Style.linkButton}
                              onClick={() =>
                                openModal(
                                  { kind: 'edit-recipient', recipient },
                                  {
                                    name: recipient.name,
                                    email: recipient.email,
                                    type: recipient.type,
                                    category: recipient.category,
                                    org: recipient.org,
                                  },
                                )
                              }
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className={Style.dangerButton}
                              onClick={() =>
                                openModal(
                                  { kind: 'remove-recipient', list, recipient },
                                  {},
                                )
                              }
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </>
      ) : null}

      {renderModal()}
    </section>
  );
};

export default MailingListManager;
